'use client';

import React, { ReactNode, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { analyticsEventEmitter } from '@portals/wb-front-analytics-tracker';
import { RtSellerFrameRoot } from '@dpa/ritm-sellers-portal';
import { AppLayout } from '@/layouts/app-layout';
import { PageLayout } from '@/layouts/page-layout';
import { Footer } from '@/_pages/categories/_components/footer';
import { useClientSearchParams } from '@/_hooks/use-client-search-params';
import { getAnalyticsExternalData, getIsSideMode } from '@/_utils';
import { usePathname } from '@/i18n/navigation';
import { CopyRights } from '@/_components/copy-rights';
import { ADVICE_LIST_PATH, TMultiPageRouteParams } from '@/_constants/routes';
import { DynamicWarningCookieBannerWrapper } from '@/_components/warning-cookie-banner-wrapper/dynamic';
import { FooterControls } from '@/_pages/categories/_components/footer/_components/footer-controls';
import { ClientEnvProvider, TClientEnvContext } from '@/contexts';
import { DynamicAppHeader } from '@/_components/client-root-layout/_components/app-header/dynamic';
import { useAnalytic } from './_hooks/use-analytic';
import { EAnalyticAbGroup, FOOTER_CONTROLS_COUNTRIES } from './_constants';
import { getIsPathWithSocials } from './_utils/get-is-path-with-socials';
import { getCategoryTree } from './_utils/get-category-tree';

type TProps = {
  children: ReactNode;
  clientEnvs: TClientEnvContext;
  categoryTree?: ReactNode;
};

export const ClientRootLayout = ({
  children,
  clientEnvs,
  categoryTree,
}: TProps) => {
  const searchParamsHookResult = useClientSearchParams();
  const params = useParams<TMultiPageRouteParams>();
  const { country, locale } = params;
  const isSideMode = getIsSideMode(searchParamsHookResult);
  const pathName = usePathname();
  const isPathWithFooter = getIsPathWithSocials(pathName);
  const canSeeFooterControls =
    !isSideMode &&
    isPathWithFooter &&
    FOOTER_CONTROLS_COUNTRIES.includes(country);
  const showCopyRights = !pathName.includes(ADVICE_LIST_PATH);
  const categoryTreeComponent = getCategoryTree(pathName, categoryTree);

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-is-side-mode',
      String(isSideMode),
    );
  });

  useEffect(() => {
    analyticsEventEmitter.setAdditionalParamsGetter(() => {
      const { idUser, idSupplier } = getAnalyticsExternalData();

      return {
        locale,
        region: country,
        idUser,
        idSupplier,
        // ab_group при отсутствии запущенного теста должен быть со значением EAnalyticAbGroup.DISABLED
        // это позволяет отличать A_GROUP от DISABLED
        // eslint-disable-next-line camelcase
        ab_group: EAnalyticAbGroup.DISABLED,
      };
    });
  }, [country, locale]);

  useEffect(() => {
    if (!analyticsEventEmitter.getIsInitialized()) {
      analyticsEventEmitter.init({
        counterId: Number(clientEnvs.analyticCountId),
      });
    }
  }, [clientEnvs.analyticCountId]);

  useAnalytic();

  return (
    <RtSellerFrameRoot>
      <ClientEnvProvider clientEnvs={clientEnvs}>
        {!isSideMode && <DynamicAppHeader />}
        <AppLayout isSideMode={isSideMode}>
          <PageLayout categoryTree={categoryTreeComponent}>
            {children}
          </PageLayout>
          <Footer>
            {canSeeFooterControls && <FooterControls />}
            {showCopyRights && <CopyRights />}
          </Footer>
          <DynamicWarningCookieBannerWrapper country={country} />
        </AppLayout>
      </ClientEnvProvider>
    </RtSellerFrameRoot>
  );
};
